import {
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import isNil from 'lodash/isNil';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  getApiFormGetFormPreviewPdf,
  getApiFormGetFormBuilderFormId,
  getApiFormGetFormBuilderOptions,
  getApiFormGetFormCategories,
  getApiFormGetFormWorkflowInitiationCategories,
  getApiFormGetCategoryRules,
  deleteApiFormDeleteForm,
  postApiFormCreateForm,
  postApiFormCopyForm,
  postApiFormReplaceFormPdf,
  postApiFormSaveFormFields,
} from 'endpoints';
import {
  FormBuilderResponseDTO,
  CreateFormRequestDTO,
  CopyFormRequestDTO,
  DeleteRequestDTO,
  None,
  FormViewDTO,
  MultiMediaResponseDTO,
  ReplaceFormPdfRequestDTO,
  SaveFormFieldsRequestDTO,
  FormCategoryDTO,
  FormBuilderOptionsDTO,
  FormCategoryRulesResponseDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  uuid,
} from 'utils/misc';
import {
  builderStateReducer,
  createDuplicatedField,
  getNextDefaultFieldName,
  createInitialFormBuilderState,
} from 'views/EMR/Settings/PdfTemplateBuilder/state/builderState';
import {
  PdfTemplateDocument,
  PdfTemplateField,
  PdfTemplateFieldType,
  PdfPageRect,
  PdfTemplateSummary,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  DEFAULT_HORIZONTAL_ALIGNMENT,
  DEFAULT_VERTICAL_ALIGNMENT,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formFields';

function hasFormSummaryChanged(
  currentTemplate: PdfTemplateSummary,
  nextTemplate?: Partial<PdfTemplateSummary>,
) {
  return nextTemplate?.id !== currentTemplate.id
    || nextTemplate?.title !== currentTemplate.title
    || nextTemplate?.status !== currentTemplate.status
    || nextTemplate?.isDraft !== currentTemplate.isDraft
    || nextTemplate?.updatedAt !== currentTemplate.updatedAt
    || nextTemplate?.pageCount !== currentTemplate.pageCount;
}

export function useGetFormCategoryRules():
  UseQueryResult<FormCategoryRulesResponseDTO, Error> {
  return usePrefetchedData<FormCategoryRulesResponseDTO>({
    key: ['form-category-rules'],
    fetchFn: getApiFormGetCategoryRules,
  });
}

export function useGetFormTemplateSettings():
  UseQueryResult<FormBuilderOptionsDTO, Error> {
  return usePrefetchedData<FormBuilderOptionsDTO>({
    key: ['form-builder-settings'],
    fetchFn: getApiFormGetFormBuilderOptions,
  });
}

export function useGetFormCategories():
  UseQueryResult<FormCategoryDTO[], Error> {
  return useQuery<FormCategoryDTO[], Error>({
    queryKey: ['form-categories'],
    queryFn: getApiFormGetFormCategories,
  });
}

export function useGetFormWorkflowInitiationCategories():
  UseQueryResult<FormCategoryDTO[], Error> {
  return useQuery<FormCategoryDTO[], Error>({
    queryKey: ['form-workflow-initiation-categories'],
    queryFn: getApiFormGetFormWorkflowInitiationCategories,
  });
}

export function useAddForm():
  UseMutationResult<
    FormViewDTO,
    Error,
    CreateFormRequestDTO
    > {
  return useMutation<FormViewDTO, Error, CreateFormRequestDTO>({
    mutationFn: (data) => postApiFormCreateForm(data),
  });
}

export function useCopyForm():
  UseMutationResult<
    FormViewDTO,
    Error,
    CopyFormRequestDTO
    > {
  return useMutation<FormViewDTO, Error, CopyFormRequestDTO>({
    mutationFn: (data) => postApiFormCopyForm(data),
  });
}

export function useDeleteForm():
  UseMutationResult<None, Error, DeleteRequestDTO> {
  return useMutation<None, Error, DeleteRequestDTO>({
    mutationFn: (data) => deleteApiFormDeleteForm(data),
  });
}

export function useReplaceFormPdf():
  UseMutationResult<None, Error, ReplaceFormPdfRequestDTO> {
  return useMutation<None, Error, ReplaceFormPdfRequestDTO>({
    mutationFn: (data) => postApiFormReplaceFormPdf(data),
  });
}

export function useGetFormBuilder(
  formId?: number,
): UseQueryResult<FormBuilderResponseDTO, Error> {
  return useQuery<FormBuilderResponseDTO, Error>({
    queryKey: ['form-builder', formId],
    queryFn: () => getApiFormGetFormBuilderFormId(formId ?? 0),
    enabled: !Number.isNaN(formId) && typeof formId === 'number',
  });
}

export function useSaveFormFields():
  UseMutationResult<None, Error, SaveFormFieldsRequestDTO> {
  return useMutation<None, Error, SaveFormFieldsRequestDTO>({
    mutationFn: (data) => postApiFormSaveFormFields(data),
  });
}

export function useGetFormPreviewPdf():
  UseMutationResult<MultiMediaResponseDTO, Error, number> {
  return useMutation<MultiMediaResponseDTO, Error, number>({
    mutationFn: (formId) => getApiFormGetFormPreviewPdf({ formId }),
  });
}

export function useFormBuilderState(
  formIdString: string | undefined,
  document: PdfTemplateDocument,
  template?: Partial<PdfTemplateSummary>,
  fields?: PdfTemplateField[],
) {
  const initialState = useMemo(
    () => createInitialFormBuilderState(formIdString, document, template, fields),
    [document, fields, template, formIdString],
  );
  const [state, dispatch] = useReducer(builderStateReducer, initialState);

  useEffect(() => {
    const documentChanged = state.document.url !== document.url
      || state.document.fileName !== document.fileName;
    const templateChanged = hasFormSummaryChanged(state.template, template);

    if (document.url && (documentChanged || templateChanged)) {
      dispatch({ type: 'loadDocument', document, template });
    }
  }, [
    document,
    state.document.fileName,
    state.document.url,
    state.template,
    template,
  ]);

  useEffect(() => {
    if (fields) {
      dispatch({ type: 'loadFields', fields });
    }
  }, [fields]);

  const selectedField = useMemo(
    () => state.fields.find((field) => field.id === state.selectedFieldId) ?? null,
    [state.fields, state.selectedFieldId],
  );

  const selectField = (
    fieldId: string | null,
    options?: { multi?: boolean },
  ) => {
    dispatch({ type: 'selectField', fieldId, multi: options?.multi });
  };

  const addField = (
    rect: PdfPageRect,
    fieldType: PdfTemplateFieldType,
    pageNumber: number,
    assignee: FormFieldAssignee = FormFieldAssignee.Patient,
  ) => {
    const fieldName = getNextDefaultFieldName(
      state.fields.map((field) => field.name),
    );
    const field = {
      id: uuid(),
      name: fieldName,
      type: fieldType,
      pageNumber,
      rect,
      required: true,
      isRequired: true,
      assignee,
      horizontalAlignment: DEFAULT_HORIZONTAL_ALIGNMENT,
      verticalAlignment: DEFAULT_VERTICAL_ALIGNMENT,
      ...(fieldType === 'radioGroup' ? {
        groupId: uuid(),
        groupName: fieldName,
      } : {}),
      ...(fieldType === 'checkboxGroup' ? {
        groupId: uuid(),
        groupName: fieldName,
      } : {}),
    };
    dispatch({
      type: 'addField',
      field,
    });

    return field;
  };

  const updateSelectedFieldType = (fieldType: PdfTemplateFieldType) => {
    dispatch({ type: 'updateSelectedFieldType', fieldType });
  };

  const removeField = (fieldId: string) => {
    dispatch({ type: 'removeField', fieldId });
  };

  const removeFields = (fieldIds: string[]) => {
    dispatch({ type: 'removeFields', fieldIds });
  };

  const updateSelectedFieldName = (fieldName: string) => {
    dispatch({ type: 'updateSelectedFieldName', fieldName });
  };

  const updateSelectedRadioGroupName = (groupName: string) => {
    dispatch({ type: 'updateSelectedRadioGroupName', groupName });
  };

  const updateSelectedCheckboxGroupName = (groupName: string) => {
    dispatch({ type: 'updateSelectedCheckboxGroupName', groupName });
  };

  const updateSelectedFieldAlias = (aliasId: string | null) => {
    dispatch({ type: 'updateSelectedFieldAlias', aliasId });
  };

  const updateSelectedFieldVerticalAlignment = (alignment: string | null) => {
    dispatch({ type: 'updateSelectedFieldVerticalAlignment', alignment });
  };

  const updateSelectedFieldHorizontalAlignment = (alignment: string | null) => {
    dispatch({ type: 'updateSelectedFieldHorizontalAlignment', alignment });
  };

  const updateSelectedFieldFontSize = (fontSize: number | null) => {
    dispatch({ type: 'updateSelectedFieldFontSize', fontSize });
  };

  const updateSelectedFieldAssignee = (assignee: FormFieldAssignee) => {
    dispatch({ type: 'updateSelectedFieldAssignee', assignee });
  };

  const updateSelectedFieldRequired = (isRequired: boolean) => {
    dispatch({ type: 'updateSelectedFieldRequired', isRequired });
  };

  const coerceFieldAssignees = (assignee: FormFieldAssignee) => {
    dispatch({ type: 'coerceFieldAssignees', assignee });
  };

  const addRadioGroupOption = (
    rect: PdfPageRect,
    pageNumber: number,
  ) => {
    const { selectedFieldId } = state;
    const selectedRadioField = state.fields.find((field) => field.id === selectedFieldId);
    if (isNil(selectedRadioField) || selectedRadioField.type !== 'radioGroup') {
      return null;
    }

    const fieldName = getNextDefaultFieldName(
      state.fields.map((field) => field.name),
    );

    const field = {
      ...selectedRadioField,
      id: uuid(),
      name: fieldName,
      pageNumber,
      rect,
      required: false,
      isRequired: false,
    };

    dispatch({
      type: 'addRadioGroupOption',
      field,
    });

    return field;
  };

  const addCheckboxGroupOption = (
    rect: PdfPageRect,
    pageNumber: number,
  ) => {
    const { selectedFieldId } = state;
    const selectedCheckboxField = state.fields.find((field) => field.id === selectedFieldId);
    if (isNil(selectedCheckboxField) || selectedCheckboxField.type !== 'checkboxGroup') {
      return null;
    }

    const fieldName = getNextDefaultFieldName(
      state.fields.map((field) => field.name),
    );

    const field = {
      ...selectedCheckboxField,
      id: uuid(),
      name: fieldName,
      pageNumber,
      rect,
      required: false,
      isRequired: false,
    };

    dispatch({
      type: 'addCheckboxGroupOption',
      field,
    });

    return field;
  };

  const updateFormTitle = (title: string) => {
    dispatch({ type: 'updateFormTitle', title });
  };

  const updateFieldRect = (fieldId: string, rect: PdfPageRect) => {
    dispatch({ type: 'updateFieldRect', fieldId, rect });
  };

  const updateFieldPosition = (
    fieldId: string,
    pageNumber: number,
    rect: PdfPageRect,
  ) => {
    dispatch({
      type: 'updateFieldPosition',
      fieldId,
      pageNumber,
      rect,
    });
  };

  const duplicateField = (field: PdfTemplateField) => {
    dispatch({
      type: 'duplicateField',
      field: createDuplicatedField(field, state.fields, uuid),
    });
  };

  const duplicateFields = (fields: PdfTemplateField[]) => {
    const duplicatedFields: PdfTemplateField[] = [];

    fields.forEach((field) => {
      duplicatedFields.push(createDuplicatedField(
        field,
        [...state.fields, ...duplicatedFields],
        uuid,
      ));
    });

    dispatch({
      type: 'duplicateFields',
      fields: duplicatedFields,
    });

    return duplicatedFields;
  };

  const replaceFields = (fields: PdfTemplateField[]) => {
    dispatch({
      type: 'replaceFields',
      fields,
    });
  };

  const restoreFieldHistoryScope = (
    scopeId: string,
    fields: PdfTemplateField[],
  ) => {
    dispatch({
      type: 'restoreFieldHistoryScope',
      scopeId,
      fields,
    });
  };

  const restoreHistorySnapshot = (
    fields: PdfTemplateField[],
    selectedFieldId: string | null,
    selectedFieldIds: string[],
  ) => {
    dispatch({
      type: 'restoreHistorySnapshot',
      fields,
      selectedFieldId,
      selectedFieldIds,
    });
  };

  const setZoom = (zoom: number) => {
    dispatch({ type: 'setZoom', zoom });
  };

  return {
    state,
    selectedField,
    addField,
    removeField,
    removeFields,
    selectField,
    updateFieldRect,
    updateFieldPosition,
    updateSelectedFieldAlias,
    updateSelectedFieldHorizontalAlignment,
    updateSelectedFieldVerticalAlignment,
    updateSelectedFieldFontSize,
    updateSelectedFieldAssignee,
    updateSelectedFieldRequired,
    coerceFieldAssignees,
    updateSelectedFieldName,
    updateSelectedRadioGroupName,
    updateSelectedCheckboxGroupName,
    updateSelectedFieldType,
    addRadioGroupOption,
    addCheckboxGroupOption,
    updateFormTitle,
    duplicateField,
    duplicateFields,
    replaceFields,
    restoreHistorySnapshot,
    restoreFieldHistoryScope,
    setZoom,
  };
}
